import React, { useState } from "react";
import styled, { keyframes } from "styled-components";
import EstadaTemporaria from "../../components/visa/EstadaTemporaria";
import ProcuraDeTrabalho from "../../components/visa/ProcuraDeTrabalho";
import Residencia from "../../components/visa/Residencia";
import VisaBanner from "../../components/visa/VisaBanner";
import VistoCPLP from "../../components/visa/VistoCPLP";
import { FiChevronDown, FiChevronUp, FiCheck, FiClock, FiFileText, FiUser, FiBriefcase, FiGlobe } from "react-icons/fi";

// Animations
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

// const gradientAnimation = keyframes`
//   0% {
//     background-position: 0% 50%;
//   }
//   50% {
//     background-position: 100% 50%;
//   }
//   100% {
//     background-position: 0% 50%;
//   }
// `;

// Design Tokens
const colors = {
  primary: "#151c48",
  primaryLight: "#2a3a8f",
  accent: "#5ab9d3",
  white: "#ffffff",
  lightBg: "#f8f9ff",
  darkBg: "#0a0f2e",
  textPrimary: "#1a1a1a",
  textSecondary: "#5f6c84",
  success: "#10b981",
  warning: "#f59e0b",
  error: "#ef4444"
};

const spacing = {
  sm: "8px",
  md: "16px",
  lg: "24px",
  xl: "32px",
  xxl: "48px",
  xxxl: "64px"
};

const borderRadius = {
  sm: "4px",
  md: "8px",
  lg: "12px",
  xl: "16px",
  xxl: "24px"
};

// Styled Components
const PremiumVisaPage = styled.div`
  width: 100%;
  background-color: ${colors.lightBg};
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  color: ${colors.textPrimary};
`;

const MainContainer = styled.div`
  width: 100%;
  max-width: 1400px;
  margin: 0 auto;
  padding: ${spacing.xxl};
  position: relative;

  @media (max-width: 992px) {
    padding: ${spacing.lg};
  }

  @media (max-width: 768px) {
    padding: ${spacing.md};
  }
`;

const CategorySection = styled.section`
  margin: ${spacing.xxxl} 0;
  animation: ${fadeIn} 0.6s ease-out;
`;

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 700;
  color: ${colors.primary};
  margin-bottom: ${spacing.xxl};
  position: relative;
  text-align: center;
  
  &::after {
    content: '';
    display: block;
    width: 100px;
    height: 4px;
    background: linear-gradient(90deg, ${colors.primary}, ${colors.accent});
    margin: ${spacing.md} auto 0;
    border-radius: 2px;
  }

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const CategoryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: ${spacing.xl};
  margin-top: ${spacing.xxl};

  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const DetailsContainer = styled.div`
  margin-top: ${spacing.xxl};
  background: ${colors.white};
  border-radius: ${borderRadius.xl};
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  transition: all 0.3s ease;
  border: 1px solid rgba(0, 0, 0, 0.05);
`;

const DetailsHeader = styled.div`
  background: linear-gradient(135deg, ${colors.primary}, ${colors.primaryLight});
  color: ${colors.white};
  padding: ${spacing.xl};
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
`;

const DetailsTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0;
  display: flex;
  align-items: center;
  gap: ${spacing.md};
  color: ${colors.white}; /* Adicionado cor branca para o título */

  svg {
    color: ${colors.accent};
  }
`;

const DetailsContent = styled.div`
  padding: ${spacing.xxl};
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${spacing.xxl};

  @media (max-width: 992px) {
    grid-template-columns: 1fr;
  }
`;

const DetailsColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.xl};
`;

const DetailCard = styled.div`
  background: ${colors.lightBg};
  border-radius: ${borderRadius.lg};
  padding: ${spacing.xl};
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.03);
`;

const DetailCardTitle = styled.h4`
  font-size: 1.2rem;
  color: ${colors.primary};
  margin-bottom: ${spacing.md};
  display: flex;
  align-items: center;
  gap: ${spacing.sm};

  svg {
    color: ${colors.accent};
  }
`;

const DetailCardText = styled.p`
  color: ${colors.textSecondary};
  line-height: 1.7;
  margin-bottom: ${spacing.md};
`;

const DetailList = styled.ul`
  margin: 0;
  padding-left: ${spacing.lg};
  list-style: none;
`;

const DetailListItem = styled.li`
  position: relative;
  padding-left: ${spacing.xl};
  margin-bottom: ${spacing.md};
  color: ${colors.textSecondary};
  line-height: 1.6;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 8px;
    width: 8px;
    height: 8px;
    background: ${colors.accent};
    border-radius: 50%;
  }
`;

const ProcessTimeline = styled.div`
  position: relative;
  padding-left: ${spacing.xl};
  margin-top: ${spacing.lg};

  &::before {
    content: '';
    position: absolute;
    left: 7px;
    top: 0;
    bottom: 0;
    width: 2px;
    background: linear-gradient(to bottom, ${colors.accent}, ${colors.primary});
  }
`;

const ProcessStep = styled.div`
  position: relative;
  padding-bottom: ${spacing.xl};
  margin-bottom: ${spacing.xl};

  &:last-child {
    margin-bottom: 0;
    padding-bottom: 0;
  }

  &::before {
    content: '';
    position: absolute;
    left: -${spacing.xl};
    top: 0;
    width: 16px;
    height: 16px;
    background: ${colors.primary};
    border-radius: 50%;
    border: 3px solid ${colors.accent};
  }
`;

const ProcessStepTitle = styled.h5`
  font-size: 1.1rem;
  color: ${colors.primary};
  margin-bottom: ${spacing.sm};
  display: flex;
  align-items: center;
  gap: ${spacing.sm};
`;

const ProcessStepText = styled.p`
  color: ${colors.textSecondary};
  line-height: 1.6;
`;

const DocumentCard = styled.div`
  background: linear-gradient(135deg, rgba(90, 185, 211, 0.1), rgba(21, 28, 72, 0.1));
  border-radius: ${borderRadius.md};
  padding: ${spacing.lg};
  margin-bottom: ${spacing.md};
  border-left: 3px solid ${colors.accent};
`;

const DocumentTitle = styled.h6`
  font-size: 1rem;
  color: ${colors.primary};
  margin-bottom: ${spacing.sm};
`;

const DocumentText = styled.p`
  font-size: 0.9rem;
  color: ${colors.textSecondary};
`;

// const CtaSection = styled.section`
//   margin: ${spacing.xxxl} 0;
//   padding: ${spacing.xxxl} ${spacing.xxl};
//   background: linear-gradient(135deg, ${colors.primary}, ${colors.darkBg});
//   border-radius: ${borderRadius.xl};
//   color: ${colors.white};
//   text-align: center;
//   position: relative;
//   overflow: hidden;

//   &::before {
//     content: '';
//     position: absolute;
//     top: -50%;
//     left: -50%;
//     width: 200%;
//     height: 200%;
//     background: radial-gradient(circle, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 70%);
//     animation: ${gradientAnimation} 15s ease infinite;
//     background-size: 400% 400%;
//   }
// `;

// const CtaTitle = styled.h2`
//   font-size: 2.5rem;
//   color: ${colors.white};
//   margin-bottom: ${spacing.lg};
//   position: relative;
//   z-index: 2;

//   @media (max-width: 768px) {
//     font-size: 2rem;
//   }
// `;

// const CtaText = styled.p`
//   font-size: 1.2rem;
//   max-width: 700px;
//   margin: 0 auto ${spacing.xxl};
//   line-height: 1.6;
//   position: relative;
//   z-index: 2;
//   opacity: 0.9;

//   @media (max-width: 768px) {
//     font-size: 1rem;
//   }
// `;

// const CtaButton = styled.button`
//   background: ${colors.white};
//   color: ${colors.primary};
//   border: none;
//   padding: ${spacing.md} ${spacing.xxl};
//   border-radius: ${borderRadius.full};
//   font-size: 1.1rem;
//   font-weight: 600;
//   cursor: pointer;
//   transition: all 0.3s ease;
//   position: relative;
//   z-index: 2;
//   box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);

//   &:hover {
//     transform: translateY(-3px);
//     box-shadow: 0 15px 30px rgba(0, 0, 0, 0.2);
//   }

//   &:active {
//     transform: translateY(1px);
//   }
// `;

function VisaPage() {
  const [activeCategory, setActiveCategory] = useState(null);
  const [expandedDetails, setExpandedDetails] = useState(false);

  const handleCategoryClick = (category) => {
    if (activeCategory === category) {
      setActiveCategory(null);
      setExpandedDetails(false);
    } else {
      setActiveCategory(category);
      setExpandedDetails(true);
    }
  };

  const renderDetails = () => {
    if (!activeCategory) return null;

    switch (activeCategory) {
      case "EstadaTemporaria":
        return (
          <>
            <DetailsHeader onClick={() => setExpandedDetails(!expandedDetails)}>
              <DetailsTitle>
                <FiClock /> Visto para Estadia Temporária
              </DetailsTitle>
              {expandedDetails ? <FiChevronUp size={24} /> : <FiChevronDown size={24} />}
            </DetailsHeader>
            
            {expandedDetails && (
              <DetailsContent>
                <DetailsColumn>
                  <DetailCard>
                    <DetailCardTitle><FiCheck /> Visão Geral</DetailCardTitle>
                    <DetailCardText>
                      O visto de estadia temporária permite que você permaneça em Portugal por até um ano. 
                      Ideal para estudos, trabalho temporário ou outras atividades que não requerem residência permanente.
                    </DetailCardText>
                  </DetailCard>
                  
                  <DetailCard>
                    <DetailCardTitle><FiBriefcase /> Tipos de Visto</DetailCardTitle>
                    <DetailList>
                      <DetailListItem>
                        <strong>Trabalho e investigação:</strong> Vários tipos de visto para trabalho sazonal, 
                        atividade independente, atividades altamente qualificadas.
                      </DetailListItem>
                      <DetailListItem>
                        <strong>Estudo:</strong> Inclui ensino secundário, licenciatura, mestrado, doutoramento.
                      </DetailListItem>
                      <DetailListItem>
                        <strong>Saúde:</strong> Tratamento médico e acompanhante.
                      </DetailListItem>
                      <DetailListItem>
                        <strong>Religiosos:</strong> Formação religiosa ou estudos em congregações reconhecidas.
                      </DetailListItem>
                      <DetailListItem>
                        <strong>Familiar:</strong> Acompanhamento de titular de visto de estada temporária.
                      </DetailListItem>
                    </DetailList>
                  </DetailCard>
                </DetailsColumn>
                
                <DetailsColumn>
                  <DetailCard>
                    <DetailCardTitle><FiFileText /> Processo de Solicitação</DetailCardTitle>
                    <ProcessTimeline>
                      <ProcessStep>
                        <ProcessStepTitle><FiCheck /> Passo 1: Documentação</ProcessStepTitle>
                        <ProcessStepText>
                          Preparar todos os documentos necessários, incluindo passaporte válido, comprovativo de meios de subsistência, seguro de saúde e documentos específicos conforme o tipo de visto.
                        </ProcessStepText>
                      </ProcessStep>
                      <ProcessStep>
                        <ProcessStepTitle><FiCheck /> Passo 2: Agendamento</ProcessStepTitle>
                        <ProcessStepText>
                          Agendar entrevista no consulado ou embaixada portuguesa no seu país de residência.
                        </ProcessStepText>
                      </ProcessStep>
                      <ProcessStep>
                        <ProcessStepTitle><FiCheck /> Passo 3: Pagamento</ProcessStepTitle>
                        <ProcessStepText>
                          Efetuar o pagamento das taxas consulares (varia conforme o tipo de visto).
                        </ProcessStepText>
                      </ProcessStep>
                      <ProcessStep>
                        <ProcessStepTitle><FiCheck /> Passo 4: Aprovação</ProcessStepTitle>
                        <ProcessStepText>
                          Aguardar a análise do pedido (tempo médio de 30-60 dias).
                        </ProcessStepText>
                      </ProcessStep>
                    </ProcessTimeline>
                  </DetailCard>
                  
                  <DetailCard>
                    <DetailCardTitle><FiUser /> Documentos Necessários</DetailCardTitle>
                    <DocumentCard>
                      <DocumentTitle>Documentos Básicos</DocumentTitle>
                      <DocumentText>
                        Passaporte válido, 2 fotos 3x4, formulário preenchido, seguro de viagem, comprovativo de alojamento em Portugal.
                      </DocumentText>
                    </DocumentCard>
                    <DocumentCard>
                      <DocumentTitle>Documentos Específicos</DocumentTitle>
                      <DocumentText>
                        Varia conforme o tipo de visto: contrato de trabalho para visto de trabalho, matrícula para visto de estudante, etc.
                      </DocumentText>
                    </DocumentCard>
                  </DetailCard>
                </DetailsColumn>
              </DetailsContent>
            )}
          </>
        );
      
      case "Residencia":
        return (
          <>
            <DetailsHeader onClick={() => setExpandedDetails(!expandedDetails)}>
              <DetailsTitle>
                <FiUser /> Visto de Residência
              </DetailsTitle>
              {expandedDetails ? <FiChevronUp size={24} /> : <FiChevronDown size={24} />}
            </DetailsHeader>
            
            {expandedDetails && (
              <DetailsContent>
                <DetailsColumn>
                  <DetailCard>
                    <DetailCardTitle><FiCheck /> Visão Geral</DetailCardTitle>
                    <DetailCardText>
                      O visto de residência permite morar em Portugal de forma permanente, com acesso ao mercado 
                      de trabalho, benefícios sociais e um caminho para a cidadania portuguesa.
                    </DetailCardText>
                  </DetailCard>
                  
                  <DetailCard>
                    <DetailCardTitle><FiBriefcase /> Tipos de Visto</DetailCardTitle>
                    <DetailList>
                      <DetailListItem>
                        <strong>Trabalho:</strong> Inclui vistos para atividades como trabalho subordinado, 
                        nómada digital, trabalho independente e atividades desportivas.
                      </DetailListItem>
                      <DetailListItem>
                        <strong>Estudo e investigação:</strong> Para investigação, ensino superior e programas de intercâmbio.
                      </DetailListItem>
                      <DetailListItem>
                        <strong>Familiar:</strong> Reagrupamento familiar e acompanhamento de familiares.
                      </DetailListItem>
                      <DetailListItem>
                        <strong>Fixação de Residência:</strong> Para reformados, religiosos e pessoas que vivem de rendimentos.
                      </DetailListItem>
                    </DetailList>
                  </DetailCard>
                </DetailsColumn>
                
                <DetailsColumn>
                  <DetailCard>
                    <DetailCardTitle><FiFileText /> Benefícios</DetailCardTitle>
                    <DetailList>
                      <DetailListItem>Acesso ao sistema de saúde público</DetailListItem>
                      <DetailListItem>Direito a trabalhar legalmente</DetailListItem>
                      <DetailListItem>Possibilidade de trazer familiares</DetailListItem>
                      <DetailListItem>Caminho para cidadania após 5 anos</DetailListItem>
                      <DetailListItem>Livrecirculação na área Schengen</DetailListItem>
                    </DetailList>
                  </DetailCard>
                  
                  <DetailCard>
                    <DetailCardTitle><FiClock /> Processo de Renovação</DetailCardTitle>
                    <DetailCardText>
                      O visto de residência é inicialmente válido por 2 anos e pode ser renovado por 
                      períodos sucessivos de 3 anos. Após 5 anos de residência legal, pode solicitar 
                      residência permanente ou cidadania portuguesa.
                    </DetailCardText>
                  </DetailCard>
                </DetailsColumn>
              </DetailsContent>
            )}
          </>
        );
      
      case "ProcuraDeTrabalho":
        return (
          <>
            <DetailsHeader onClick={() => setExpandedDetails(!expandedDetails)}>
              <DetailsTitle>
                <FiBriefcase /> Visto para Procura de Trabalho
              </DetailsTitle>
              {expandedDetails ? <FiChevronUp size={24} /> : <FiChevronDown size={24} />}
            </DetailsHeader>
            
            {expandedDetails && (
              <DetailsContent>
                <DetailsColumn>
                  <DetailCard>
                    <DetailCardTitle><FiCheck /> Visão Geral</DetailCardTitle>
                    <DetailCardText>
                      Permite buscar emprego em Portugal por até 6 meses (prorrogável por mais 60 dias). 
                      Se conseguir trabalho, pode solicitar autorização de residência sem sair do país.
                    </DetailCardText>
                  </DetailCard>
                  
                  <DetailCard>
                    <DetailCardTitle><FiBriefcase /> Requisitos</DetailCardTitle>
                    <DetailList>
                      <DetailListItem>Passaporte válido</DetailListItem>
                      <DetailListItem>Seguro de saúde</DetailListItem>
                      <DetailListItem>Comprovativo de meios de subsistência</DetailListItem>
                      <DetailListItem>Registo criminal</DetailListItem>
                      <DetailListItem>Inscrição no IEFP para prorrogação</DetailListItem>
                    </DetailList>
                  </DetailCard>
                </DetailsColumn>
                
                <DetailsColumn>
                  <DetailCard>
                    <DetailCardTitle><FiFileText /> Vantagens</DetailCardTitle>
                    <DetailList>
                      <DetailListItem>Tempo para conhecer o mercado de trabalho português</DetailListItem>
                      <DetailListItem>Possibilidade de entrevistas presenciais</DetailListItem>
                      <DetailListItem>Transição facilitada para visto de trabalho</DetailListItem>
                      <DetailListItem>Experiência de vida em Portugal antes de compromisso definitivo</DetailListItem>
                    </DetailList>
                  </DetailCard>
                  
                  <DetailCard>
                    <DetailCardTitle><FiClock /> Estatísticas</DetailCardTitle>
                    <DetailCardText>
                      <strong>Taxa de sucesso:</strong> 68% dos titulares conseguem emprego qualificado em 4 meses.
                      <br />
                      <strong>Setores com mais oportunidades:</strong> Tecnologia, Saúde, Engenharia e Turismo.
                    </DetailCardText>
                  </DetailCard>
                </DetailsColumn>
              </DetailsContent>
            )}
          </>
        );
      
      case "CPLP":
        return (
          <>
            <DetailsHeader onClick={() => setExpandedDetails(!expandedDetails)}>
              <DetailsTitle>
                <FiGlobe /> Visto CPLP
              </DetailsTitle>
              {expandedDetails ? <FiChevronUp size={24} /> : <FiChevronDown size={24} />}
            </DetailsHeader>
            
            {expandedDetails && (
              <DetailsContent>
                <DetailsColumn>
                  <DetailCard>
                    <DetailCardTitle><FiCheck /> Visão Geral</DetailCardTitle>
                    <DetailCardText>
                      Concedido para cidadãos de países da CPLP, permite permanecer em Portugal por até 6 meses 
                      (prorrogável por mais 60 dias) para trabalho, estudo ou fixação de residência.
                    </DetailCardText>
                  </DetailCard>
                  
                  <DetailCard>
                    <DetailCardTitle><FiBriefcase /> Requisitos Financeiros</DetailCardTitle>
                    <DetailList>
                      <DetailListItem>
                        <strong>Primeiro adulto:</strong> 100% do salário mínimo (€820 em 2024)
                      </DetailListItem>
                      <DetailListItem>
                        <strong>Outros adultos:</strong> 50% do salário mínimo cada
                      </DetailListItem>
                      <DetailListItem>
                        <strong>Crianças:</strong> 30% do salário mínimo cada
                      </DetailListItem>
                    </DetailList>
                  </DetailCard>
                </DetailsColumn>
                
                <DetailsColumn>
                  <DetailCard>
                    <DetailCardTitle><FiFileText /> Documentos Comprovativos</DetailCardTitle>
                    <DetailList>
                      <DetailListItem>Subvenções ou bolsas de estudo</DetailListItem>
                      <DetailListItem>Contrato ou promessa de contrato de trabalho</DetailListItem>
                      <DetailListItem>Contrato de sociedade ou proposta de prestação de serviços</DetailListItem>
                      <DetailListItem>Extratos bancários ou declaração de rendimentos</DetailListItem>
                    </DetailList>
                  </DetailCard>
                  
                  <DetailCard>
                    <DetailCardTitle><FiClock /> Vantagens Exclusivas</DetailCardTitle>
                    <DetailList>
                      <DetailListItem>Processo simplificado para cidadãos da CPLP</DetailListItem>
                      <DetailListItem>Taxas consulares reduzidas</DetailListItem>
                      <DetailListItem>Possibilidade de regularização em território português</DetailListItem>
                      <DetailListItem>Acesso facilitado ao mercado de trabalho</DetailListItem>
                    </DetailList>
                  </DetailCard>
                </DetailsColumn>
              </DetailsContent>
            )}
          </>
        );
      
      default:
        return null;
    }
  };

  return (
    <PremiumVisaPage>
      <VisaBanner />
      
      <MainContainer>
        <CategorySection>
          <SectionTitle>Tipos de Visto para Portugal</SectionTitle>
          
          <CategoryGrid>
            <div onClick={() => handleCategoryClick("EstadaTemporaria")}>
              <EstadaTemporaria active={activeCategory === "EstadaTemporaria"} />
            </div>
            <div onClick={() => handleCategoryClick("Residencia")}>
              <Residencia active={activeCategory === "Residencia"} />
            </div>
            <div onClick={() => handleCategoryClick("ProcuraDeTrabalho")}>
              <ProcuraDeTrabalho active={activeCategory === "ProcuraDeTrabalho"} />
            </div>
            <div onClick={() => handleCategoryClick("CPLP")}>
              <VistoCPLP active={activeCategory === "CPLP"} />
            </div>
          </CategoryGrid>
        </CategorySection>

        {activeCategory && (
          <DetailsContainer>
            {renderDetails()}
          </DetailsContainer>
        )}

        {/* <CtaSection>
          <CtaTitle>Precisa de Ajuda com Seu Visto?</CtaTitle>
          <CtaText>
            Nossos especialistas podem guiá-lo através de todo o processo, desde a escolha do visto 
            correto até a preparação da documentação necessária.
          </CtaText>
          <CtaButton>Fale com um Consultor</CtaButton>
        </CtaSection> */}
      </MainContainer>
    </PremiumVisaPage>
  );
}

export default VisaPage;